import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import {
	Component,
	ContentChild,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	signal,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
	WritableSignal
} from '@angular/core';
import { NavigationEnd, Router, Event, RouterLinkActive, RouterLink } from '@angular/router';
import { switchMap, tap, filter, takeUntil, Subscription } from 'rxjs';

import { CypressTagDirective } from '@cjm/cypress/core';
import { BrowserService } from '@cjm/shared/core';
import { MediaQueryMax, MediaQueryMin, OnDestroyComponent, ScreenSize } from '@cjm/shared/types';
import { MediaQueryDirective } from '@cjm/shared/ui/device';

import { CJMMenuItemEntity } from '../../types';

@Component({
	selector: 'cjm-menu',
	templateUrl: 'menu.component.html',
	styleUrl: './menu.component.scss',
	host: {
		'[class.cjm-menu--open]': 'isOpen()'
	},
	imports: [MediaQueryDirective, CypressTagDirective, RouterLinkActive, RouterLink, NgTemplateOutlet]
})
export class MenuComponent extends OnDestroyComponent implements OnInit, OnDestroy {
	@HostBinding('class.c-menu') public readonly hasRootClass: boolean = true;
	/**
	 * An array of items we wish to display in the menu
	 */
	@Input() public menuItems: CJMMenuItemEntity[] = [];
	/**
	 * An array of items we wish to display in the menu
	 */
	@Input() public secondaryMenuItems: CJMMenuItemEntity[] = [];
	/**
	 * This attribute provides a way to define the text of the button label
	 * for the mobile version of the navigation.
	 *
	 * Default value is set to 'Menu'.
	 */
	@Input() public menuLabel: string = 'Menu';
	/**
	 * This attribute will emit the state of the menu when the menu is toggled on mobile.
	 */
	@Output() public readonly toggled: EventEmitter<boolean> = new EventEmitter<boolean>();

	/**
	 * A template for content of the anchor tag
	 * This allows the parent to provide a custom template for the content of the anchor tag
	 * which can be used to translate the menu items in the parent component
	 */
	@ContentChild('itemTmpl', { static: false }) itemTemplate: TemplateRef<any>;
	/**
	 * A template for the content of the primary menu.
	 */
	@ViewChild('primaryMenuTmpl', { static: true }) public primaryMenuTmpl: TemplateRef<any>;

	public readonly mediaQueryMax: typeof MediaQueryMax = MediaQueryMax;
	public readonly mediaQueryMin: typeof MediaQueryMin = MediaQueryMin;
	public readonly isOpen: WritableSignal<boolean> = signal(false);
	private readonly overLayConfig: OverlayConfig = new OverlayConfig({
		hasBackdrop: true,
		backdropClass: 'c-menu__backdrop',
		panelClass: 'c-menu-overlay'
	});
	private overlayRef: OverlayRef;
	private overlayBackdropClickSubscription: Subscription;

	constructor(
		private readonly browserService: BrowserService,
		private readonly router: Router,
		private readonly overlay: Overlay,
		private readonly viewContainerRef: ViewContainerRef
	) {
		super();
	}

	public ngOnInit(): void {
		// Denis: On mobile browsers, close the menu when the user navigates to a new page
		this.browserService.width$
			.pipe(
				filter((width: number) => width <= ScreenSize.Desktop),
				switchMap(() => this.router.events),
				filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
				tap(() => {
					this.closeMenu();
				}),
				takeUntil(this.destroyed$)
			)
			.subscribe();

		// Denis: When scaling to desktop, close the menu overlay
		this.browserService.width$
			.pipe(
				filter((width: number) => width > ScreenSize.Desktop),
				tap(() => {
					this.closeMenu();
				}),
				takeUntil(this.destroyed$)
			)
			.subscribe();
	}

	/**
	 * Toggles the state of the menu. Removes scroll when overlay is active.
	 */
	public handleMenuButtonClick(): void {
		if (this.overlayBackdropClickSubscription) {
			this.overlayBackdropClickSubscription.unsubscribe();
		}

		if (!this.isOpen()) {
			this.openMenu();

			return;
		}

		this.closeMenu();
	}

	/**
	 * openMenu
	 *
	 * The openMenu method opens the menu by
	 * 1. setting the `isOpen` signal to true,
	 * 2. creating an overlay,
	 * 3. attaching a template portal to the overlay,
	 * 4. subscribing to the backdrop click event.
	 */
	private openMenu(): void {
		// Denis: Set the menu state to open
		this.isOpen.set(true);

		// Denis: Create the overlay with the specified configuration
		this.overlayRef = this.overlay.create({
			...this.overLayConfig,
			scrollStrategy: this.overlay.scrollStrategies.block()
		});

		// Denis: Attach the template portal to the overlay
		this.overlayRef.attach(new TemplatePortal(this.primaryMenuTmpl, this.viewContainerRef));

		// Denis: Subscribe to the backdrop click event to close the menu when the backdrop is clicked
		this.overlayRef
			.backdropClick()
			.pipe(
				tap(() => {
					this.closeMenu();
				})
			)
			.subscribe();

		this.toggled.emit(true);
	}

	/**
	 * closeMenu
	 *
	 * The closeMenu closes the menu by
	 * 1. disposing of the overlay,
	 * 2. setting the `isOpen` signal to false.
	 */
	private closeMenu(): void {
		// Denis: Dispose of the overlay if it exists
		if (this.overlayRef) {
			this.overlayRef.dispose();
		}

		// Denis: Set the menu state to closed
		this.isOpen.set(false);

		this.toggled.emit(false);
	}
}
